/**
 * Cadastros > Administradores (Vitor, 15/10, item 1 do que faltava do sistema antigo). Quem entra no painel: incluir
 * alguem, corrigir o nome, definir uma senha nova e desativar ou reativar. Ate aqui isso so existia no terminal
 * (`pnpm operador`); as rotas ja estavam prontas no backend (`/v1/admin/operadores`).
 *
 * So o dono da conta ve esta tela, porque so ele pode mexer nessas rotas hoje. O papel nao aparece: pela ADR-011 todo
 * mundo e administrador, e o novo nasce com o mesmo acesso dos outros. Desativar tira a pessoa na hora (as sessoes
 * abertas caem); senha nova tambem derruba as sessoes dela.
 */
import { Aviso, Botao, Campo, CampoSenha, CabecalhoDePagina, Carregando, Pilha, Selo, Tabela, Vazio, formatarDataHora, formatarDia } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { erroDe, useApi } from "../api";
import { useSessao } from "../sessao";
import { DialogoDeAcao } from "./DialogoDeAcao";
import { usePagina } from "../pagina";

type Administrador = { id: string; email: string; nome: string; role: string; ativo: boolean; criado_em: string; ultimo_login_em: string | null };
type Acao = { tipo: "nome" | "senha" | "desativar" | "reativar"; a: Administrador } | { tipo: "incluir" };

/** Uma senha forte pra passar pra pessoa: 14 caracteres sem os que se confundem (0, O, 1, l, I). */
export function senhaSugerida(): string {
  const letras = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = crypto.getRandomValues(new Uint8Array(14));
  return Array.from(bytes, (b) => letras[b % letras.length]).join("");
}

const emailValido = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

export function PaginaAdministradores() {
  const pag = usePagina("pagina");
  const { ler, postar, mudar } = useApi();
  const qc = useQueryClient();
  const { estado } = useSessao();
  const eu = estado.status === "logado" ? estado.usuario.id : null;
  const q = useQuery({ queryKey: ["administradores"], queryFn: () => ler<{ itens: Administrador[] }>("/v1/admin/operadores") });
  const [acao, setAcao] = useState<Acao | null>(null);
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [feito, setFeito] = useState<string>();
  const fechar = () => setAcao(null);
  const atualizar = (msg: string) => { setFeito(msg); void qc.invalidateQueries({ queryKey: ["administradores"] }); };
  const abrir = (a: Acao) => {
    setFeito(undefined);
    setNome(a.tipo === "nome" ? a.a.nome : "");
    setEmail("");
    setSenha(a.tipo === "incluir" || a.tipo === "senha" ? senhaSugerida() : "");
    setAcao(a);
  };
  const itens = q.data?.itens ?? [];
  const ativos = itens.filter((a) => a.ativo).length;
  const alvo = acao && acao.tipo !== "incluir" ? acao.a : null;
  const senhaOk = senha.length >= 8;
  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina
        titulo="Administradores"
        descricao="Quem entra neste painel. Todos têm o mesmo acesso. Desativar tira a pessoa na hora."
        acoes={<Botao onClick={() => abrir({ tipo: "incluir" })}>Incluir administrador</Botao>}
      />
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {q.isError && <Aviso tipo="erro">{erroDe(q.error)}</Aviso>}
      {q.isLoading ? <Carregando compacto texto="Buscando os administradores" /> : (
        <Tabela
          paginacao={pag}
          linhas={itens}
          chave={(a) => a.id}
          vazio={<Vazio titulo="Ninguém por aqui">Inclua o primeiro administrador.</Vazio>}
          colunas={[
            { chave: "nome", rotulo: "Administrador", celula: (a) => <><strong>{a.nome}{a.id === eu ? " (você)" : ""}</strong><span className="mtc-tabela__apoio">{a.email}</span></> },
            { chave: "situacao", rotulo: "Situação", celula: (a) => <Selo tom={a.ativo ? "sucesso" : "neutro"}>{a.ativo ? "Ativo" : "Desativado"}</Selo> },
            { chave: "acesso", rotulo: "Último acesso", celula: (a) => (a.ultimo_login_em ? <span className="mtc-mono">{formatarDataHora(a.ultimo_login_em)}</span> : <span className="mtc-texto-2">Nunca entrou</span>) },
            { chave: "desde", rotulo: "Incluído em", celula: (a) => <span className="mtc-mono">{formatarDia(a.criado_em)}</span> },
            { chave: "acoes", rotulo: "", celula: (a) => (
              <div className="mtc-linha" style={{ gap: "var(--e-2)", flexWrap: "wrap", justifyContent: "flex-end" }}>
                <Botao tamanho="pequeno" variante="fantasma" aria-label={`Editar o nome de ${a.nome}`} onClick={() => abrir({ tipo: "nome", a })}>Editar nome</Botao>
                {a.ativo && <Botao tamanho="pequeno" variante="fantasma" aria-label={`Definir senha nova pra ${a.nome}`} onClick={() => abrir({ tipo: "senha", a })}>Senha nova</Botao>}
                {a.id !== eu && (a.ativo
                  ? <Botao tamanho="pequeno" variante="secundario" aria-label={`Desativar ${a.nome}`} onClick={() => abrir({ tipo: "desativar", a })}>Desativar</Botao>
                  : <Botao tamanho="pequeno" variante="secundario" aria-label={`Reativar ${a.nome}`} onClick={() => abrir({ tipo: "reativar", a })}>Reativar</Botao>)}
              </div>
            ) },
          ]}
        />
      )}
      {!q.isLoading && itens.length > 0 && <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>{ativos} {ativos === 1 ? "ativo" : "ativos"} de {itens.length}.</span>}

      <DialogoDeAcao
        aberto={acao?.tipo === "incluir"}
        aoFechar={fechar}
        titulo="Incluir administrador"
        descricao="A pessoa entra com este e-mail e a senha abaixo. Passe a senha por um canal seu; ela pode trocar depois pelo Esqueci minha senha."
        botao="Incluir"
        podeEnviar={nome.trim().length >= 2 && emailValido(email) && senhaOk}
        aoEnviar={() => postar("/v1/admin/operadores", { nome: nome.trim(), email: email.trim(), senha, role: "ADMIN" })}
        aoConcluir={() => atualizar(`${nome.trim()} foi incluído. Já pode entrar com ${email.trim().toLowerCase()}.`)}
      >
        <Campo rotulo="Nome" valor={nome} aoMudar={setNome} autoComplete="off" />
        <Campo rotulo="E-mail" type="email" valor={email} aoMudar={setEmail} autoComplete="off" erro={email && !emailValido(email) ? "Confira o e-mail" : undefined} />
        <CampoSenha rotulo="Senha inicial" valor={senha} aoMudar={setSenha} autoComplete="new-password" dica="Sugerimos uma senha forte. Pode trocar; o mínimo são 8 caracteres." erro={senha && !senhaOk ? "Pelo menos 8 caracteres" : undefined} />
      </DialogoDeAcao>

      <DialogoDeAcao
        aberto={acao?.tipo === "nome"}
        aoFechar={fechar}
        titulo="Editar nome"
        descricao={alvo?.email}
        botao="Salvar"
        podeEnviar={nome.trim().length >= 2 && nome.trim() !== alvo?.nome}
        aoEnviar={() => mudar(`/v1/admin/operadores/${alvo!.id}`, { nome: nome.trim() })}
        aoConcluir={() => atualizar("Nome atualizado.")}
      >
        <Campo rotulo="Nome" valor={nome} aoMudar={setNome} autoComplete="off" />
      </DialogoDeAcao>

      <DialogoDeAcao
        aberto={acao?.tipo === "senha"}
        aoFechar={fechar}
        titulo="Definir senha nova"
        descricao={alvo ? `${alvo.nome} sai de onde estiver conectado e entra de novo com esta senha.` : undefined}
        botao="Definir senha"
        podeEnviar={senhaOk}
        aoEnviar={() => postar(`/v1/admin/operadores/${alvo!.id}/senha`, { senha })}
        aoConcluir={() => atualizar(`Senha nova definida pra ${alvo!.nome}.`)}
      >
        <CampoSenha rotulo="Senha nova" valor={senha} aoMudar={setSenha} autoComplete="new-password" dica="Passe a senha por um canal seu." erro={senha && !senhaOk ? "Pelo menos 8 caracteres" : undefined} />
      </DialogoDeAcao>

      <DialogoDeAcao
        aberto={acao?.tipo === "desativar" || acao?.tipo === "reativar"}
        aoFechar={fechar}
        titulo={acao?.tipo === "desativar" ? "Desativar administrador" : "Reativar administrador"}
        descricao={alvo ? (acao?.tipo === "desativar" ? `${alvo.nome} sai do painel agora e não consegue mais entrar. O que ${alvo.nome.split(" ")[0]} fez continua na trilha. Dá pra reativar depois.` : `${alvo.nome} volta a entrar no painel com a senha que já tinha.`) : undefined}
        botao={acao?.tipo === "desativar" ? "Desativar" : "Reativar"}
        variante={acao?.tipo === "desativar" ? "perigo" : "primario"}
        aoEnviar={() => mudar(`/v1/admin/operadores/${alvo!.id}`, { ativo: acao?.tipo === "reativar" })}
        aoConcluir={() => atualizar(acao?.tipo === "desativar" ? `${alvo!.nome} foi desativado.` : `${alvo!.nome} foi reativado.`)}
      />
    </Pilha>
  );
}
